/**
 * Sesion de Supabase disponible para toda la app, y el `ApiClient` que la usa.
 *
 * Por que el `ApiClient` se construye aca y no en un modulo suelto: necesita un
 * callback que devuelva el access token **vigente**. Ese callback es
 * `supabase.auth.getSession()`, que ademas dispara el refresh si el token esta
 * por vencer. Atarlos en el mismo lugar evita el bug clasico de capturar el
 * token una vez y mandarlo vencido para siempre.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";

import { ApiClient } from "../api/client";
import type { AppConfig } from "../config";

export interface AuthState {
  /** Sesion vigente, o `null` si no hay nadie logueado. */
  readonly session: Session | null;
  /** `true` mientras se resuelve la sesion inicial desde el storage. */
  readonly loading: boolean;
  /** Cliente HTTP ya cableado al token de esta sesion. */
  readonly api: ApiClient;
  readonly signInWithPassword: (email: string, password: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export interface AuthProviderProps {
  readonly config: AppConfig;
  readonly supabase: SupabaseClient;
  readonly children: ReactNode;
}

export function AuthProvider({ config, supabase, children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // La sesion tambien vive en una ref para que el token provider del ApiClient
  // no dependa del ciclo de render: el cliente se crea una sola vez y siempre
  // lee lo ultimo.
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;

  useEffect(() => {
    let active = true;

    // Sesion inicial: puede venir de localStorage o del fragmento de la URL.
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    // A partir de aca Supabase avisa de login, logout y de cada refresh de
    // token. Escuchar esto es lo que mantiene el Bearer al dia sin polling.
    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        if (!active) return;
        setSession(nextSession);
        setLoading(false);
      },
    );

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [supabase]);

  const api = useMemo(
    () =>
      new ApiClient(config.apiBaseUrl, async () => {
        // getSession() refresca el token si esta vencido o por vencer. Usar
        // sessionRef.current.access_token directamente seria mas rapido y
        // mandaria tokens muertos.
        const { data } = await supabase.auth.getSession();
        return data.session?.access_token ?? null;
      }),
    [config.apiBaseUrl, supabase],
  );

  const value = useMemo<AuthState>(
    () => ({
      session,
      loading,
      api,
      signInWithPassword: async (email: string, password: string) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw new Error(error.message);
      },
      signOut: async () => {
        await supabase.auth.signOut();
      },
    }),
    [session, loading, api, supabase],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Acceder a la sesion.
 *
 * @throws Si se usa fuera de `AuthProvider`. Es deliberado: devolver un estado
 * vacio haria que un componente mal ubicado se vea "deslogueado" en vez de
 * fallar, y ese bug es carisimo de encontrar.
 */
export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error("useAuth() requiere estar dentro de <AuthProvider>");
  }
  return value;
}
